import React from 'react';
import './SolutionsPage.css';
import { Link } from 'react-router-dom';

const SolutionsPage = () => {
  const solutions = [
    {
      id: 1,
      category: 'GENERATIVE AI',
      title: 'Large Language Models & GPT Integration',
      description: 'Deploy enterprise-grade LLMs for intelligent document processing, automated content generation, and conversational AI. Integrate GPT-4, Claude, and custom fine-tuned models for domain-specific applications with advanced RAG (Retrieval-Augmented Generation) capabilities.',
      image: '/images/llm-solution.jpg',
      features: ['Custom Model Fine-tuning', 'Multi-modal AI', 'Enterprise Security', 'API Integration']
    },
    {
      id: 2,
      category: 'COMPUTER VISION',
      title: 'Advanced Visual Intelligence Systems',
      description: 'State-of-the-art computer vision solutions powered by deep learning for object detection, facial recognition, defect detection, and real-time video analytics. Achieve 99%+ accuracy in quality control and automated visual inspection.',
      image: '/images/computer-vision-enterprise.jpg',
      features: ['Object Detection', 'OCR & Document AI', 'Video Analytics', 'Quality Inspection']
    },
    {
      id: 3,
      category: 'PREDICTIVE ANALYTICS',
      title: 'Machine Learning & Forecasting',
      description: 'Advanced predictive models using gradient boosting, neural networks, and ensemble methods. Forecast demand, predict churn, optimize pricing, and identify anomalies with enterprise-scale ML pipelines and MLOps infrastructure.',
      image: '/images/predictive-ml.jpg',
      features: ['Time Series Forecasting', 'Anomaly Detection', 'Churn Prediction', 'MLOps Pipeline']
    },
    {
      id: 4,
      category: 'NATURAL LANGUAGE',
      title: 'NLP & Sentiment Analysis',
      description: 'Process and understand human language at scale with transformer-based models. Extract insights from customer feedback, automate document classification, perform entity recognition, and analyze sentiment across multiple languages.',
      image: '/images/nlp-solution.jpg',
      features: ['Sentiment Analysis', 'Named Entity Recognition', 'Text Classification', 'Language Translation']
    },
    {
      id: 5,
      category: 'RECOMMENDATION SYSTEMS',
      title: 'Personalization & Recommendation Engines',
      description: 'Build sophisticated recommendation systems using collaborative filtering, deep learning, and hybrid approaches. Power e-commerce, content platforms, and enterprise applications with real-time personalized recommendations.',
      image: '/images/recommendation-system.jpg',
      features: ['Collaborative Filtering', 'Deep Learning Models', 'Real-time Recommendations', 'A/B Testing']
    },
    {
      id: 6,
      category: 'AUTOMATION',
      title: 'Intelligent Process Automation (IPA)',
      description: 'Combine RPA with AI to automate complex business processes. Implement intelligent document processing, automated decision-making, and workflow optimization with cognitive automation capabilities.',
      image: '/images/intelligent-automation.jpg',
      features: ['RPA Integration', 'Document Processing', 'Workflow Automation', 'Decision Intelligence']
    },
    {
      id: 7,
      category: 'DATA SCIENCE',
      title: 'Advanced Analytics & Data Mining',
      description: 'Uncover hidden patterns and insights with advanced statistical analysis, clustering algorithms, and dimensionality reduction techniques. Build comprehensive data science platforms with end-to-end analytics capabilities.',
      image: '/images/data-analytics.jpg',
      features: ['Clustering & Segmentation', 'Feature Engineering', 'Statistical Modeling', 'Big Data Processing']
    },
    {
      id: 8,
      category: 'AI INFRASTRUCTURE',
      title: 'MLOps & AI Platform Engineering',
      description: 'Design and implement scalable AI infrastructure with model versioning, automated training pipelines, monitoring, and deployment. Build enterprise MLOps platforms on AWS, Azure, or GCP with Kubernetes orchestration.',
      image: '/images/mlops-platform.jpg',
      features: ['Model Management', 'CI/CD for ML', 'Model Monitoring', 'Scalable Deployment']
    },
    {
      id: 9,
      category: 'CONVERSATIONAL AI',
      title: 'Advanced Chatbots & Virtual Assistants',
      description: 'Build intelligent conversational interfaces with natural language understanding, context awareness, and multi-turn dialogue management. Deploy voice-enabled assistants and chatbots across multiple channels.',
      image: '/images/chatbot-solution.jpg',
      features: ['Intent Recognition', 'Multi-channel Support', 'Voice Integration', 'Context Management']
    },
    {
      id: 10,
      category: 'EDGE AI',
      title: 'Edge Computing & IoT Intelligence',
      description: 'Deploy AI models on edge devices for real-time inference with minimal latency. Optimize models for embedded systems, mobile devices, and IoT sensors with model quantization and pruning techniques.',
      image: '/images/edge-ai.jpg',
      features: ['Model Optimization', 'Real-time Inference', 'IoT Integration', 'Low-latency Processing']
    }
  ];

  return (
    <div className="solutions-page">
      {/* Hero Section */}
      <section className="solutions-hero">
        <div className="solutions-hero-content">
          <h1 className="solutions-hero-title">Enterprise AI Solutions</h1>
          <p className="solutions-hero-subtitle">
            Cutting-edge artificial intelligence solutions designed for modern enterprises. 
            Transform your business with state-of-the-art AI technologies.
          </p>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="solutions-grid-section">
        {solutions.map((solution, index) => (
          <div key={solution.id} className="solution-item">
            <div className={`solution-container ${index % 2 === 1 ? 'reverse' : ''}`}>
              <div className="solution-text-side">
                <div className="solution-content">
                  <span className="solution-category">{solution.category}</span>
                  <h2 className="solution-title">{solution.title}</h2>
                  <p className="solution-description">{solution.description}</p>
                  <div className="solution-features">
                    {solution.features.map((feature, idx) => (
                      <span key={idx} className="solution-feature-tag">{feature}</span>
                    ))}
                  </div>
                  <Link to="/contact-us" className="solution-cta-button">
                    GET STARTED →
                  </Link>
                </div>
              </div>
              <div className="solution-image-side">
                <img 
                  src={solution.image} 
                  alt={solution.title}
                  className="solution-image"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/800x600/f3f4f6/1f2937?text=' + encodeURIComponent(solution.category);
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* CTA Section */}
      <section className="solutions-cta-section">
        <div className="solutions-cta-content">
          <h2 className="solutions-cta-title">Ready to Transform Your Business?</h2>
          <p className="solutions-cta-text">
            Let's discuss how our AI solutions can drive innovation and growth for your enterprise.
          </p>
          <Link to="/contact-us" className="solutions-cta-primary-button">
            Schedule a Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SolutionsPage;
